import { AssetService } from './asset';

export interface TelemetryRow {
    latitude: number;
    longitude: number;
    heading: number;
    input1: boolean;
    input2: boolean;
    input3: boolean;
    input4: boolean;
    assetId: string;
    dateOccurred: string;
    speed: number;
}

export interface TelemetryResponse {
    data: TelemetryRow[];
    nextCursor: string | null;
    hasMore: boolean;
}

export interface LoaderOptions {
    orgId: string;
    start: string;
    end: string;
    assetType: number;
    limit?: number;

    /**
     * Called every time a page loads
     */
    onChunk?: (rows: TelemetryRow[], index: number) => void;

    /**
     * Called when all data is finished loading
     */
    onComplete?: () => void;

    /**
     * Optional fetch override (for auth, headers, etc.)
     */
    fetcher?: typeof fetch;
}

export class TelemetryKeysetLoader {
    private orgId: string;
    private start: string;
    private end: string;
    private limit: number;
    private assetType: number;
    private cursor: string | null = null;

    private onChunk?: (rows: TelemetryRow[], index: number) => void;
    private onComplete?: () => void;
    private abortController: AbortController | null = null;

    private pageIndex = 0;
    private isLoading = false;
    private hasMore = true;
    private baseUrl: string;
    private jwt: string | null;

    constructor(options: LoaderOptions) {
        if (window.location.host == 'localhost') {
            this.baseUrl = 'https://localhost:8080';
        } else {
            this.baseUrl = 'https://www.dynamiciotdata.com:8080';
        }
        this.jwt = localStorage.getItem('vision-jwt');

        this.orgId = options.orgId;
        this.start = options.start;
        this.end = options.end;
        this.assetType = options.assetType;
        this.limit = options.limit ?? 1000;

        this.onChunk = options.onChunk;
        this.onComplete = options.onComplete;
    }

    async loadNext(): Promise<void> {
        if (!this.abortController || this.abortController.signal.aborted) {
            this.abortController = new AbortController();
        }
        if (this.isLoading || !this.hasMore) return;

        this.isLoading = true;

        try {
            const url = new URL(this.baseUrl + '/api/' + this.orgId + '/telemetry/slice');
            url.searchParams.append('start', this.start);
            url.searchParams.append('end', this.end);
            url.searchParams.append('assetType', String(this.assetType));
            url.searchParams.append('limit', String(this.limit));

            if (this.cursor) {
                url.searchParams.append('lastSeenDate', this.cursor);
            }

            const res = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + this.jwt,
                },
                signal: this.abortController?.signal,
            });

            const json: TelemetryResponse = await res.json();

            this.hasMore = json.hasMore;

            if (json.data?.length) {
                this.cursor = json.nextCursor;

                this.onChunk?.(json.data, this.pageIndex);
                this.pageIndex++;
            }

            if (!this.hasMore) {
                this.onComplete?.();
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            if (err?.name === 'AbortError') {
                // expected cancel
                return;
            }
            throw err;
        } finally {
            this.isLoading = false;
        }
    }

    async loadAll(): Promise<void> {
        if (!this.abortController || this.abortController.signal.aborted) {
            this.abortController = new AbortController();
        }

        while (this.hasMore) {
            await this.loadNext();
        }

        //this.onComplete?.();
    }

    reset(cursor: string | null = null): void {
        this.cursor = cursor;
        this.pageIndex = 0;
        this.hasMore = true;
        this.isLoading = false;
    }

    public cancel(): void {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        this.isLoading = false;
        this.hasMore = false;
    }
}
